<template>
  <DetailsAccordion
    :error-description="i18n(translations.errorDescription)"
    :status
    @refresh="refresh"
  >
    <template #icon>
      <IconQuality />
    </template>
    <template #title>
      <span v-if="status === 'error'">
        {{ i18n(translations.defaultTitle) }}
      </span>
      <span v-else-if="isInWarranty" class="text-static-success-hi">
        {{ i18n(translations.inWarrantyTitle) }}
      </span>
      <span v-else class="text-static-danger-hi">
        {{ i18n(translations.outWarrantyTitle) }}
      </span>
    </template>
    <template #description>
      <span data-allow-mismatch="text">
        <template v-if="!isInWarranty">
          {{ i18n(translations.outOfWarrantyDescription) }}
        </template>
        <template v-if="isInSellerWarranty">
          {{ sellerWarrantyDescription }}
        </template>
        <template v-if="isInSellerWarrantyExtension">
          {{ sellerWarrantyExtensionDescription }}
        </template>
        <template v-if="isInBackmarketWarranty">
          {{ backmarketWarrantyDescription }}
        </template>
      </span>
    </template>
    <template #body>
      <ul class="space-y-16">
        <li v-if="hasSellerWarranty">
          <p class="font-weight-body-2-bold">
            {{ i18n(translations.sellerWarrantyTitle) }}
          </p>
          <p>{{ sellerWarrantyDescription }}</p>
        </li>
        <li v-if="hasSellerWarrantyExtension">
          <p class="font-weight-body-2-bold">
            {{ i18n(translations.sellerWarrantyExtensionTitle) }}
          </p>
          <p>{{ sellerWarrantyExtensionDescription }}</p>
        </li>
        <li v-if="hasBackmarketWarranty">
          <p class="font-weight-body-2-bold">
            {{ i18n(translations.backmarketWarrantyTitle) }}
          </p>
          <p>{{ backmarketWarrantyDescription }}</p>
        </li>
      </ul>
    </template>
  </DetailsAccordion>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isFutureDate } from '@backmarket/utils/date/isFutureDate'
import { IconQuality } from '@ds/icons/IconQuality'

import { useOrderlineDetails } from '~/scopes/customer-care/pages/request/composables/useOrderlineDetails'

import DetailsAccordion from '../DetailsAccordion/DetailsAccordion.vue'

import translations from './Warranty.translations'

const i18n = useI18n()

const {
  orderDetails: {
    data: { orderline },
    status,
    refresh,
  },
} = useOrderlineDetails()

const hasSellerWarranty = computed(
  () => orderline.value?.coverage?.merchantWarranty,
)
const hasSellerWarrantyExtension = computed(
  () => orderline.value?.coverage?.merchantWarrantyExtension,
)
const hasBackmarketWarranty = computed(
  () => orderline.value?.coverage?.backmarketWarranty,
)

const isInSellerWarranty = computed(
  () =>
    orderline.value?.coverage &&
    hasSellerWarranty.value &&
    isFutureDate(orderline.value?.coverage.merchantWarranty.endDate),
)
const isInSellerWarrantyExtension = computed(
  () =>
    orderline.value?.coverage &&
    hasSellerWarrantyExtension.value &&
    isFutureDate(orderline.value?.coverage.merchantWarrantyExtension.endDate),
)
const isInBackmarketWarranty = computed(
  () =>
    orderline.value?.coverage &&
    hasBackmarketWarranty.value &&
    isFutureDate(orderline.value?.coverage.backmarketWarranty.endDate),
)
const isInWarranty = computed(
  () =>
    isInSellerWarranty.value ||
    isInSellerWarrantyExtension.value ||
    isInBackmarketWarranty.value,
)

const sellerWarrantyDescription = computed(() =>
  orderline.value?.coverage
    ? i18n(translations.sellerWarrantyDescription, {
        duration: orderline.value?.coverage.merchantWarranty.duration,
        date: new Date(orderline.value.coverage.merchantWarranty.endDate),
      })
    : '',
)
const sellerWarrantyExtensionDescription = computed(() =>
  orderline.value?.coverage
    ? i18n(translations.sellerWarrantyExtensionDescription, {
        duration: orderline.value?.coverage.merchantWarrantyExtension.duration,
        date: new Date(
          orderline.value.coverage.merchantWarrantyExtension.endDate,
        ),
      })
    : '',
)
const backmarketWarrantyDescription = computed(() =>
  orderline.value?.coverage
    ? i18n(translations.backmarketWarrantyDescription, {
        duration: orderline.value.coverage.backmarketWarranty.duration,
        date: new Date(orderline.value.coverage.backmarketWarranty.endDate),
      })
    : '',
)
</script>
